import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FieldLabel } from '@puckeditor/core';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { Alert, Button } from 'reactstrap';

import { showModal } from '../../../../actions';
import {
  APIgetPageCollectionFiles,
  APIgetPageCollections,
} from '../../../../api/Pages';
import { thumbnailPlaceholder } from '../../../../helpers/Files';
import { usePageId } from '../../../../helpers/PageIdContext';

const collectionShape = PropTypes.shape({
  id: PropTypes.string,
  uuid: PropTypes.string,
  name: PropTypes.string,
  featured_file: PropTypes.object,
  files: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
});

const CollectionPicker = ({ value, onChange, onShowModal }) => {
  const intl = useIntl();
  const pageId = usePageId();
  const [hydratedCollection, setHydratedCollection] = useState(null);
  const [isCollectionEmpty, setIsCollectionEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!value?.uuid || !pageId) {
      setHydratedCollection(null);
      return () => {
        cancelled = true;
      };
    }

    const hasThumb = !!value?.featured_file?.urls?.thumbnail;
    const hasName = !!value?.name;
    if (hasThumb && hasName) {
      setHydratedCollection(null);
      return () => {
        cancelled = true;
      };
    }

    APIgetPageCollections(pageId, [value.uuid])
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res.data?.data) ? res.data.data : [];
        const matched = items.find(item => item.uuid === value.uuid);
        setHydratedCollection(matched || null);
      })
      .catch(() => {
        if (!cancelled) {
          setHydratedCollection(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [value, pageId]);

  useEffect(() => {
    let cancelled = false;

    if (!value?.uuid || !pageId) {
      setIsCollectionEmpty(false);
      return () => {
        cancelled = true;
      };
    }

    APIgetPageCollectionFiles(pageId, value.uuid, { page: 1, per_page: 1 })
      .then((res) => {
        if (cancelled) return;

        const items = res.data?.data?.data ?? res.data?.data ?? [];

        setIsCollectionEmpty(items.length === 0);
      })
      .catch(() => {
        if (!cancelled) setIsCollectionEmpty(false);
      });

    return () => {
      cancelled = true;
    };
  }, [value?.uuid, pageId]);

  const handleAdd = (selected) => {
    onChange({ id: uuidv4(), ...selected });
  };

  const handleRemove = () => onChange(null);

  const openModal = () => {
    onShowModal('CollectionSelectModal', {
      className: 'modal-collection-select',
      backdrop: true,
      handleCollectionSelect: handleAdd,
    });
  };

  const displayCollection = hydratedCollection
    ? { ...value, ...hydratedCollection }
    : value;
  const collectionThumb = displayCollection?.featured_file?.urls?.thumbnail || thumbnailPlaceholder;
  const collectionName = displayCollection?.name || intl.formatMessage({ defaultMessage: 'Collection' });

  return (
    <div className="puck-collection-picker-label">
      <FieldLabel label={intl.formatMessage({ defaultMessage: 'Collection' })}>
        {!displayCollection && (
          <Button size="sm" color="primary" onClick={openModal} className="w-100">
            <i className="far fa-folder-open me-1" />
            {intl.formatMessage({ defaultMessage: 'Select Collection' })}
          </Button>
        )}
        {displayCollection && (
          <div>
            <div className="puck-feed-collection-item">
              <img
                src={collectionThumb}
                alt={collectionName}
                className="puck-feed-collection-thumb"
              />
              <span className="puck-feed-collection-name">{collectionName}</span>
              <Button
                size="sm"
                color="link"
                className="puck-feed-collection-remove"
                onClick={handleRemove}
                title={intl.formatMessage({ defaultMessage: 'Remove' })}
              >
                <i className="far fa-trash mr-0" />
              </Button>
            </div>
            {isCollectionEmpty && (
              <Alert color="warning">
                {intl.formatMessage({ defaultMessage: 'This Collection has no files.' })}
              </Alert>
            )}
          </div>
        )}
      </FieldLabel>
    </div>
  );
};

CollectionPicker.propTypes = {
  value: collectionShape,
  onChange: PropTypes.func.isRequired,
  onShowModal: PropTypes.func.isRequired,
};

CollectionPicker.defaultProps = {
  value: null,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
});

export const ConnectedCollectionPicker = connect(null, mapDispatchToProps)(CollectionPicker);
